import * as Yup from "yup";
import { ButtonProps } from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { Autocomplete } from "../../../components/Autocomplete";
import { DatePicker } from "../../../components/DatePicker";
import { Dialog } from "../../../components/Dialog";
import { Person } from "../../../types/person";
import { useStyles } from "../styles";
import { parseISO } from "date-fns";
import { useMutation } from "react-query";
import { updatePerson } from "../../../services/person";
import { useToast } from "../../../hooks/useToast";
import { GENRE } from "../../../utils/constants";

type EditPersonProps = {
  person: Person;
  open: boolean;
  onClose: () => void;
};

type EditPersonType = Person & {
  reason: string;
  document: string;
};

const validationSchema = Yup.object().shape({
  nome: Yup.string().required("Nome é obrigatório"),
  documento: Yup.number().required("Documento é obrigatório"),
  reason: Yup.string().required("Motivo é obrigatório"),
  document: Yup.number().required("Documento é obrigatório"),
});

export function EditPerson({ onClose, open, person }: EditPersonProps) {
  const styles = useStyles();

  const toast = useToast();

  const putPerson = useMutation(
    async (data: EditPersonType) => {
      const response = await updatePerson(data);
      return response;
    },
    {
      onSuccess: () => {
        toast.show("Cliente atualizado com sucesso!", { severity: "success" });
        formik.resetForm();
        onClose();
      },
      onError: () => {
        toast.show("Erro ao atualiza o cliente!", { severity: "error" });
      },
    }
  );

  const handleSubmit = useCallback(
    async (data) => {
      await putPerson.mutateAsync({ ...data, id_pessoa: person?.id_pessoa });
    },
    [putPerson, person]
  );

  const formik = useFormik<EditPersonType>({
    initialValues: {} as EditPersonType,
    validationSchema,
    validateOnChange: false,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (person) {
      formik.setFieldValue("nome", person?.nome);
      formik.setFieldValue("documento", person?.documento);
      if (person?.data_nascimento) {
        formik.setFieldValue(
          "data_nascimento",
          parseISO(person?.data_nascimento?.replace("Z", ""))
        );
      } else {
        formik.setFieldValue("data_nascimento", new Date());
      }

      if (person?.sexo) {
        formik.setFieldValue(
          "sexo",
          GENRE.find((genre) => genre.value === person?.sexo)
        );
      }

      formik.setFieldValue("rua", person?.rua);
      formik.setFieldValue("bairro", person?.bairro);
      formik.setFieldValue("cidade", person?.cidade);
      formik.setFieldValue("uf", person?.uf);
      formik.setFieldValue("cep", person?.cep);
      formik.setFieldValue("complemento", person?.complemento);

      formik.setFieldValue("celular1", person?.celular1);
      formik.setFieldValue("telefone1", person?.telefone1);
      formik.setFieldValue("email1", person?.email1);
      formik.setFieldValue("blacklist_email", person?.blacklist_email);
      formik.setFieldValue("blacklist_sms", person?.blacklist_sms);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person]);

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        children: "Voltar",
        onClick: onClose,
        disabled: formik.isSubmitting,
      },
      {
        onClick: () => formik.handleSubmit(),
        children: formik.isSubmitting ? "Enviado solicitação" : "Editar",
        variant: "contained",
        disabled: formik.isSubmitting,
      },
    ];
  }, [formik, onClose]);

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      title={person?.nome}
      open={open}
      actions={actions}
      onClose={onClose}
    >
      <Stack className={styles.rootContentRow} spacing={4}>
        <Stack spacing={2} direction="row">
          <TextField
            autoFocus
            label="Nome"
            fullWidth
            size="small"
            value={formik.values.nome}
            helperText={formik.errors.nome}
            error={Boolean(formik.errors.nome)}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("nome", value)
            }
          />
          <TextField
            label="Documento"
            fullWidth
            size="small"
            value={formik.values.documento}
            helperText={formik.errors.documento}
            error={Boolean(formik.errors.documento)}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("documento", value)
            }
          />
          <DatePicker
            fullWidth
            size="small"
            label="Data de nascimento"
            onChange={(date) => formik.setFieldValue("data_nascimento", date)}
            value={formik.values.data_nascimento}
          />
        </Stack>
        <TextField
          label="Logradouro"
          fullWidth
          size="small"
          value={formik.values.rua}
          onChange={({ target: { value } }) =>
            formik.setFieldValue("rua", value)
          }
        />
        <Stack spacing={2} direction="row">
          <TextField
            label="Bairro"
            fullWidth
            size="small"
            value={formik.values.bairro}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("bairro", value)
            }
          />
          <TextField
            label="Cidade"
            fullWidth
            size="small"
            value={formik.values.cidade}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("cidade", value)
            }
          />
          <TextField
            label="UF"
            fullWidth
            size="small"
            value={formik.values.uf}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("uf", value)
            }
          />
          <TextField
            label="CEP"
            fullWidth
            size="small"
            value={formik.values.cep}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("cep", value)
            }
          />
        </Stack>

        <TextField label="Complemento" fullWidth size="small" />
        <Stack spacing={2} direction="row">
          <Autocomplete
            fullWidth
            size="small"
            label="Gênero"
            options={GENRE}
            getOptionLabel={(option) => option.label}
            onChange={(_, value) => formik.setFieldValue("sexo", value)}
          />
          <TextField
            label="Celular"
            fullWidth
            size="small"
            value={formik.values.celular1}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("celular1", value)
            }
          />
          <TextField
            label="Telefone"
            fullWidth
            size="small"
            value={formik.values.telefone1}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("telefone1", value)
            }
          />
          <TextField
            label="Email"
            fullWidth
            size="small"
            value={formik.values.email1}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("email1", value)
            }
          />
        </Stack>
        <Stack spacing={2} direction="row">
          <FormControlLabel
            onChange={(_, value) =>
              formik.setFieldValue("blacklist_email", value)
            }
            checked={!formik.values.blacklist_email}
            control={<Checkbox />}
            label="Deseja receber oferta via email ?"
          />
          <FormControlLabel
            onChange={(_, value) =>
              formik.setFieldValue("blacklist_sms", value)
            }
            checked={!formik.values.blacklist_sms}
            control={<Checkbox />}
            label="Deseja receber oferta via sms ?"
          />
        </Stack>
        <Stack spacing={2}>
          <TextField
            label="Motivo da atualização"
            fullWidth
            size="small"
            multiline
            rows={5}
            helperText={formik.errors.reason}
            error={Boolean(formik.errors.reason)}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("reason", value)
            }
          />
          <TextField
            label="Documento do responsável"
            fullWidth
            size="small"
            helperText={formik.errors.document}
            error={Boolean(formik.errors.document)}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("document", value)
            }
          />
        </Stack>
      </Stack>
    </Dialog>
  );
}
